import { useState, useMemo } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Collapse from "@mui/material/Collapse";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import BarChartIcon from "@mui/icons-material/BarChart";
import GetAppIcon from "@mui/icons-material/GetApp";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CircularProgress from "@mui/material/CircularProgress";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";

/**
 * Componente de tabla genérica reutilizable para reportes
 * Soporta paginación, filas expandibles, y acciones personalizadas
 */
function TablaReporte({
  titulo = "Reporte",
  columnas = [],
  datos = [],
  expandible = false,
  renderExpandido = null,
  onVerGrafico = null,
  onExportarExcel = null,
  onExportarPDF = null,
  loading = false,
  paginacion = true,
  filasPorPaginaOpciones = [10, 25, 50, 100],
  filasPorPaginaInicial = 25,
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(filasPorPaginaInicial);
  const [filasExpandidas, setFilasExpandidas] = useState({});

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleFila = (id) => {
    setFilasExpandidas((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Datos paginados
  const datosPaginados = useMemo(() => {
    if (!paginacion) return datos;

    const inicio = page * rowsPerPage;
    const fin = inicio + rowsPerPage;
    return datos.slice(inicio, fin);
  }, [datos, page, rowsPerPage, paginacion]);

  if (loading) {
    return (
      <Card>
        <SuiBox p={3} textAlign="center">
          <CircularProgress />
          <SuiTypography variant="body2" textColor="text" sx={{ mt: 2 }}>
            Cargando datos...
          </SuiTypography>
        </SuiBox>
      </Card>
    );
  }

  if (!datos || datos.length === 0) {
    return (
      <Card>
        <SuiBox p={3} textAlign="center">
          <SuiTypography variant="body2" textColor="text">
            No hay datos disponibles para mostrar
          </SuiTypography>
        </SuiBox>
      </Card>
    );
  }

  return (
    <Card>
      {/* Header con acciones */}
      <SuiBox p={2} display="flex" justifyContent="space-between" alignItems="center">
        <SuiTypography variant="h6">{titulo}</SuiTypography>
        <SuiBox display="flex" gap={1.5}>
          {onVerGrafico && (
            <Tooltip title="Ver gráfico">
              <SuiButton
                variant="gradient"
                color="info"
                size="medium"
                iconOnly
                onClick={onVerGrafico}
              >
                <BarChartIcon sx={{ color: "white" }} />
              </SuiButton>
            </Tooltip>
          )}

          {onExportarExcel && (
            <Tooltip title="Exportar a Excel">
              <SuiButton
                variant="gradient"
                color="success"
                size="medium"
                iconOnly
                onClick={onExportarExcel}
              >
                <GetAppIcon sx={{ color: "white" }} />
              </SuiButton>
            </Tooltip>
          )}

          {onExportarPDF && (
            <Tooltip title="Exportar a PDF">
              <SuiButton
                variant="gradient"
                color="error"
                size="medium"
                iconOnly
                onClick={onExportarPDF}
              >
                <PictureAsPdfIcon sx={{ color: "white" }} />
              </SuiButton>
            </Tooltip>
          )}
        </SuiBox>
      </SuiBox>

      {/* Tabla */}
      <TableContainer sx={{ maxHeight: 600, overflow: "auto" }}>
        <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableBody>
            {/* Row de Headers */}
            <TableRow
              sx={{
                backgroundColor: "#FDB45C",
                position: "sticky",
                top: 0,
                zIndex: 100,
              }}
            >
              {expandible && (
                <TableCell
                  width={50}
                  sx={{ backgroundColor: "#FDB45C", minWidth: "50px", maxWidth: "50px" }}
                />
              )}
              {columnas.map((columna, index) => (
                <TableCell
                  key={index}
                  align={columna.align || "left"}
                  sx={{
                    backgroundColor: "#FDB45C",
                    color: "#000",
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                    fontSize: "0.875rem",
                    width: columna.width || "auto",
                    minWidth: columna.width || "auto",
                  }}
                >
                  {columna.header}
                </TableCell>
              ))}
            </TableRow>

            {/* Filas de datos */}
            {datosPaginados.map((fila, filaIndex) => {
              const filaId = fila.id || fila[columnas[0]?.field] || filaIndex;
              const estaExpandida = filasExpandidas[filaId];

              return (
                <>
                  <TableRow
                    key={filaId}
                    hover
                    sx={{
                      "&:nth-of-type(odd)": { backgroundColor: "action.hover" },
                    }}
                  >
                    {expandible && (
                      <TableCell>
                        <IconButton size="small" onClick={() => toggleFila(filaId)}>
                          {estaExpandida ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                    )}

                    {columnas.map((columna, colIndex) => {
                      let valor = fila[columna.field];

                      // Aplicar formato si existe
                      if (columna.format && valor !== undefined && valor !== null) {
                        valor = columna.format(valor, fila);
                      }

                      // Aplicar renderizado personalizado si existe
                      if (columna.render) {
                        valor = columna.render(fila);
                      }

                      return (
                        <TableCell
                          key={colIndex}
                          align={columna.align || "left"}
                          sx={{
                            width: columna.width || "auto",
                            minWidth: columna.width || "auto",
                            whiteSpace: columna.noWrap !== false ? "nowrap" : "normal",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {valor !== undefined && valor !== null ? valor : "-"}
                        </TableCell>
                      );
                    })}
                  </TableRow>

                  {/* Fila expandible */}
                  {expandible && renderExpandido && (
                    <TableRow>
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={columnas.length + 1}
                      >
                        <Collapse in={estaExpandida} timeout="auto" unmountOnExit>
                          <SuiBox p={2}>{renderExpandido(fila)}</SuiBox>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginación */}
      {paginacion && (
        <TablePagination
          rowsPerPageOptions={filasPorPaginaOpciones}
          component="div"
          count={datos.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      )}
    </Card>
  );
}

TablaReporte.propTypes = {
  titulo: PropTypes.string,
  columnas: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.string.isRequired,
      field: PropTypes.string.isRequired,
      align: PropTypes.oneOf(["left", "center", "right"]),
      width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      format: PropTypes.func,
      render: PropTypes.func,
    })
  ).isRequired,
  datos: PropTypes.array.isRequired,
  expandible: PropTypes.bool,
  renderExpandido: PropTypes.func,
  onVerGrafico: PropTypes.func,
  onExportarExcel: PropTypes.func,
  onExportarPDF: PropTypes.func,
  loading: PropTypes.bool,
  paginacion: PropTypes.bool,
  filasPorPaginaOpciones: PropTypes.array,
  filasPorPaginaInicial: PropTypes.number,
};

export default TablaReporte;
